import { defineStore } from 'pinia'
import projectService from '@/services/project.service'
import type { Project, ProjectFilters, ProjectFormData } from '@/types/project.types'
import type { SortOrder } from '@/types/api.types'

interface ProjectState {
  projects: Project[]
  selectedProject: Project | null
  loading: boolean
  filters: ProjectFilters
  pagination: {
    page: number
    pageSize: number
    total: number
  }
}

export const useProjectStore = defineStore('projects', {
  state: (): ProjectState => ({
    projects: [],
    selectedProject: null,
    loading: false,
    filters: {},
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0
    }
  }),

  getters: {
    activeProjects: (state) => state.projects.filter(p => p.status === 'active'),
    
    projectById: (state) => (id: string) =>
      state.projects.find(p => p.id === id),

    projectsByStatus: (state) => (status: string) =>
      state.projects.filter(p => p.status === status)
  },

  actions: {
    async fetchProjects() {
      this.loading = true
      try {
        const response = await projectService.getProjects({
          ...this.filters,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        this.projects = response.data
        this.pagination.total = response.total
      } finally {
        this.loading = false
      }
    },

    async fetchProject(id: string) {
      this.loading = true
      try {
        this.selectedProject = await projectService.getProject(id)
      } finally {
        this.loading = false
      }
    },

    async createProject(data: ProjectFormData) {
      const project = await projectService.createProject(data)
      this.projects.unshift(project)
      return project
    },

    async updateProject(id: string, data: Partial<ProjectFormData>) {
      const updated = await projectService.updateProject(id, data)
      const index = this.projects.findIndex(p => p.id === id)
      if (index !== -1) {
        this.projects[index] = updated
      }
      if (this.selectedProject?.id === id) {
        this.selectedProject = updated
      }
      return updated
    },

    async deleteProject(id: string) {
      await projectService.deleteProject(id)
      this.projects = this.projects.filter(p => p.id !== id)
      if (this.selectedProject?.id === id) {
        this.selectedProject = null
      }
    },

    async updateProjectStatus(id: string, status: string) {
      const updated = await projectService.updateProjectStatus(id, status)
      const index = this.projects.findIndex(p => p.id === id)
      if (index !== -1) {
        this.projects[index] = updated
      }
      if (this.selectedProject?.id === id) {
        this.selectedProject = updated
      }
      return updated
    },

    setFilters(filters: ProjectFilters) {
      this.filters = filters
      this.pagination.page = 1
    },

    setSort(sortBy: string, sortOrder: SortOrder) {
      this.filters.sort_by = sortBy
      this.filters.sort_order = sortOrder
      this.pagination.page = 1
    },

    setPage(page: number) {
      this.pagination.page = page
    }
  }
})
